import { useMutation, useQuery } from '@apollo/client';
import type {
  CreateWorkoutFeedbackMutation,
  CreateWorkoutFeedbackMutationVariables,
  WorkoutFeedbackStatusQuery,
  WorkoutFeedbackStatusQueryVariables,
} from 'app/types/graphql';
import { RunAgainAnswer } from 'app/types/graphql';
import type { WorkoutFeedbackPayload } from 'app/ui/components/molecules/workout-feedback/types';
import { useWorkoutFeedbackLabels } from 'app/ui/components/molecules/workout-feedback/use-workout-feedback-labels';
import {
  CreateWorkoutFeedback,
  WorkoutFeedbackStatus,
} from 'app/ui/components/molecules/workout-feedback/workout-feedback.gql';
import { WorkoutFeedbackModal } from 'app/ui/components/molecules/workout-feedback/workout-feedback-modal';

interface WorkoutFeedbackControllerProps {
  lessonId: string;
  fromWhiteboard: boolean;
  onClose: () => void;
}

export const WorkoutFeedbackController = ({ lessonId, fromWhiteboard, onClose }: WorkoutFeedbackControllerProps) => {
  const labels = useWorkoutFeedbackLabels();

  const { data } = useQuery<WorkoutFeedbackStatusQuery, WorkoutFeedbackStatusQueryVariables>(WorkoutFeedbackStatus, {
    fetchPolicy: 'network-only',
    skip: !fromWhiteboard,
    variables: { lessonId },
  });

  const [createWorkoutFeedback] = useMutation<CreateWorkoutFeedbackMutation, CreateWorkoutFeedbackMutationVariables>(
    CreateWorkoutFeedback,
  );

  const hasExistingFeedback = (data?.allWorkoutFeedback?.length ?? 0) > 0;
  const open = fromWhiteboard && data !== undefined && !hasExistingFeedback;

  const handleSubmit = async (payload: WorkoutFeedbackPayload) => {
    const result = await createWorkoutFeedback({
      variables: {
        input: {
          easeOfFollowing: payload.easeOfFollowing,
          feedbackTags: payload.feedbackTags,
          freeText: payload.freeText,
          lesson: { id: lessonId },
          overallRating: payload.overallRating,
          runAgain: payload.runAgain === null ? null : RunAgainAnswer[payload.runAgain],
          skipped: payload.skipped,
        },
      },
    });
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      onClose();
    }
  };

  return <WorkoutFeedbackModal open={open} onOpenChange={handleOpenChange} labels={labels} onSubmit={handleSubmit} />;
};
